// Fortælling 2 — synlig afskrift, dynamiske beats via API, huller i flow.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type {
  KeyboardLayoutId,
  PracticeMode,
  RoundResult,
  Trin,
} from '../../types';
import { getStory2Seed } from '../../data/story2/seeds';
import type { StoryScript } from '../../data/stories/types';
import { playSuccessChime } from '../../lib/audio/successChime';
import type { PreparedRound } from '../../lib/lessonGenerator';
import {
  beatSlotsFilled,
  getBlankHint,
  parseBeatSegments,
  resolveStoryTemplate,
  syncMorphologyForSlots,
  type StorySegment,
  type StorySlotMorph,
} from '../../lib/story';
import {
  fetchStory2Health,
  fetchStory2Step,
  type Story2ApiMode,
} from '../../lib/story2Api';
import { buildStory2Script, mergeBeatIntoScript } from '../../lib/story2Runtime';
import { useEnterAction } from '../../lib/useEnterAction';
import { useRoundInput } from '../../lib/useRoundInput';
import { isPrintableTypingKey, shouldIgnoreKeyEvent } from '../../lib/typingKey';
import Keyboard from '../Keyboard';
import SpaceAdvanceHint from '../SpaceAdvanceHint';

interface StoryRound2Props {
  round: PreparedRound;
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  strictCase?: boolean;
  allowAltLayer?: boolean;
  onComplete: (
    result: RoundResult,
    action: 'dashboard' | 'another',
  ) => void;
}

type SegmentPhase = 'typingLiteral' | 'typingBlank' | 'loading';

interface StatsAccum {
  totalKeystrokes: number;
  correctChars: number;
  mistakes: number;
  backspaces: number;
  errorsByKey: Record<string, number>;
  responseTimes: Record<string, number[]>;
  durationMs: number;
  hintsUsed: boolean;
}

function emptyStats(): StatsAccum {
  return {
    totalKeystrokes: 0,
    correctChars: 0,
    mistakes: 0,
    backspaces: 0,
    errorsByKey: {},
    responseTimes: {},
    durationMs: 0,
    hintsUsed: false,
  };
}

function mergeStats(into: StatsAccum, r: RoundResult): void {
  into.totalKeystrokes += r.totalKeystrokes;
  into.correctChars += r.correctChars;
  into.mistakes += r.mistakes;
  into.backspaces += r.backspaces;
  into.durationMs += r.durationMs;
  if (r.hintsUsed) into.hintsUsed = true;
  for (const [k, v] of Object.entries(r.errorsByKey)) {
    into.errorsByKey[k] = (into.errorsByKey[k] ?? 0) + v;
  }
  for (const [k, samples] of Object.entries(r.responseTimes)) {
    into.responseTimes[k] = [...(into.responseTimes[k] ?? []), ...samples];
  }
}

function statsToResult(
  accum: StatsAccum,
  meta: {
    roundId: string;
    roundTitle: string;
    mode: PracticeMode;
    targetLength: number;
    completed: boolean;
  },
): RoundResult {
  const accuracy =
    accum.totalKeystrokes === 0
      ? 1
      : accum.correctChars / accum.totalKeystrokes;
  const wpm =
    accum.durationMs <= 0
      ? 0
      : accum.correctChars / 5 / (accum.durationMs / 60_000);

  return {
    roundId: meta.roundId,
    title: meta.roundTitle,
    mode: meta.mode,
    targetLength: meta.targetLength,
    durationMs: accum.durationMs,
    wpm,
    accuracy,
    correctChars: accum.correctChars,
    totalKeystrokes: accum.totalKeystrokes,
    mistakes: accum.mistakes,
    backspaces: accum.backspaces,
    errorsByKey: accum.errorsByKey,
    responseTimes: accum.responseTimes,
    completed: meta.completed,
    hintsUsed: accum.hintsUsed,
    presentationKind: 'story2',
  };
}

function blankDisplayWidth(maxLength: number): number {
  return Math.min(12, Math.max(6, maxLength));
}

export default function StoryRound2({
  round,
  trin,
  keyboardLayout,
  strictCase,
  allowAltLayer,
  onComplete,
}: StoryRound2Props) {
  const presentation = round.template.presentation;
  const seedId =
    presentation?.kind === 'story2' ? presentation.seedId : null;
  const seed = seedId ? getStory2Seed(seedId) : undefined;
  const mode = round.template.mode;
  const unlockedKeys = trin.unlockedKeys;

  const [apiMode, setApiMode] = useState<Story2ApiMode | null>(null);
  const [script, setScript] = useState<StoryScript | null>(() =>
    seed ? buildStory2Script(seed, [], {}) : null,
  );
  const [hasStarted, setHasStarted] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [beatIndex, setBeatIndex] = useState(0);
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [phase, setPhase] = useState<SegmentPhase>('typingLiteral');
  const [slotValues, setSlotValues] = useState<Record<string, string>>({});
  const [slotMorph, setSlotMorph] = useState<Record<string, StorySlotMorph>>(
    {},
  );
  const [completedLines, setCompletedLines] = useState<string[]>([]);
  const [storyComplete, setStoryComplete] = useState(false);
  const [blankBuffer, setBlankBuffer] = useState('');
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [dynamicBeatSeq, setDynamicBeatSeq] = useState(0);
  const statsRef = useRef(emptyStats());
  const targetLenRef = useRef(0);
  const slotValuesRef = useRef(slotValues);
  slotValuesRef.current = slotValues;
  const slotMorphRef = useRef(slotMorph);
  slotMorphRef.current = slotMorph;
  const completedLinesRef = useRef(completedLines);
  completedLinesRef.current = completedLines;
  const scriptRef = useRef(script);
  scriptRef.current = script;
  const fetchingRef = useRef(false);

  const segmentParseOpts = useMemo(
    () => ({ keepSegmentBoundaries: true as const }),
    [],
  );

  useEffect(() => {
    void fetchStory2Health().then(setApiMode);
  }, []);

  const currentBeat = script?.beats[beatIndex];
  const segments = useMemo(() => {
    if (!script || !currentBeat) return [];
    return parseBeatSegments(
      currentBeat.template,
      slotValues,
      script,
      slotMorph,
      segmentParseOpts,
    );
  }, [script, currentBeat, slotValues, slotMorph, segmentParseOpts]);

  const currentSegment = segments[segmentIndex];
  const blankHint =
    script && currentSegment?.kind === 'blank'
      ? getBlankHint(script, currentSegment.id)
      : undefined;

  useEffect(() => {
    if (!hasStarted || !currentSegment) return;
    if (phase !== 'loading') {
      setPhase(
        currentSegment.kind === 'blank' ? 'typingBlank' : 'typingLiteral',
      );
      return;
    }
    // Afslut loading når et nyt dynamisk beat er klar (efter åbningslinjer).
    const openingCount = seed?.openingBeats.length ?? 0;
    if (beatIndex >= openingCount) {
      setPhase(
        currentSegment.kind === 'blank' ? 'typingBlank' : 'typingLiteral',
      );
    }
  }, [
    beatIndex,
    segmentIndex,
    hasStarted,
    currentSegment?.kind,
    phase,
    seed?.openingBeats.length,
  ]);

  useEffect(() => {
    if (!script) return;
    syncMorphologyForSlots(slotValues, script, setSlotMorph);
  }, [slotValues, script]);

  const buildRoundResult = useCallback(
    (): RoundResult =>
      statsToResult(statsRef.current, {
        roundId: round.template.id,
        roundTitle: round.template.title,
        mode,
        targetLength: targetLenRef.current,
        completed: true,
      }),
    [round.template.id, round.template.title, mode],
  );

  const finishStory = useCallback(() => {
    setStoryComplete(true);
  }, []);

  const exitToDashboard = useCallback(() => {
    onComplete(buildRoundResult(), 'dashboard');
  }, [onComplete, buildRoundResult]);

  const playAnotherStory = useCallback(() => {
    onComplete(buildRoundResult(), 'another');
  }, [onComplete, buildRoundResult]);

  const fetchNextBeat = useCallback(
    async (lines: string[], slots: Record<string, string>) => {
      if (!seed || fetchingRef.current) return false;
      fetchingRef.current = true;
      setPhase('loading');
      setFetchError(null);

      const openingCount = seed.openingBeats.length;
      const isStart =
        lines.length >= openingCount &&
        scriptRef.current!.beats.length <= openingCount;

      try {
        const result = await fetchStory2Step({
          action: isStart ? 'start' : 'continue',
          seedId: seed.id,
          unlockedKeys,
          slots,
          completedLines: lines,
        });

        setApiMode(result.mode);

        if (result.done || !result.beat) {
          finishStory();
          return true;
        }

        const beatId = `dyn-${dynamicBeatSeq}`;
        setDynamicBeatSeq((n) => n + 1);
        const prev = scriptRef.current;
        if (!prev) return false;
        const merged = mergeBeatIntoScript(prev, result.beat!, beatId);
        const newBeat = merged.beats[merged.beats.length - 1]!;
        const firstSeg = parseBeatSegments(
          newBeat.template,
          slotValuesRef.current,
          merged,
          slotMorphRef.current,
          { keepSegmentBoundaries: true },
        )[0];
        setScript(merged);
        setBeatIndex(merged.beats.length - 1);
        setSegmentIndex(0);
        setPhase(
          firstSeg?.kind === 'blank' ? 'typingBlank' : 'typingLiteral',
        );
        return true;
      } catch (e) {
        setFetchError(
          e instanceof Error
            ? e.message
            : 'Kunne ikke hente næste linje.',
        );
        setPhase(
          currentSegment?.kind === 'blank'
            ? 'typingBlank'
            : 'typingLiteral',
        );
        return false;
      } finally {
        fetchingRef.current = false;
      }
    },
    [seed, unlockedKeys, dynamicBeatSeq, finishStory],
  );

  const finishBeat = useCallback(
    async (newSlots: Record<string, string>) => {
      if (!script || !currentBeat) return;
      if (!beatSlotsFilled(currentBeat.template, newSlots)) return;

      const resolved = resolveStoryTemplate(
        currentBeat.template,
        newSlots,
        script,
        slotMorph,
      );
      const newLines = [...completedLinesRef.current, resolved];
      setCompletedLines(newLines);
      setBlankBuffer('');

      const nextBeat = beatIndex + 1;
      if (nextBeat < script.beats.length) {
        const nextBeatDef = script.beats[nextBeat]!;
        const firstSeg = parseBeatSegments(
          nextBeatDef.template,
          newSlots,
          script,
          slotMorphRef.current,
          segmentParseOpts,
        )[0];
        setBeatIndex(nextBeat);
        setSegmentIndex(0);
        setPhase(
          firstSeg?.kind === 'blank' ? 'typingBlank' : 'typingLiteral',
        );
        return;
      }

      await fetchNextBeat(newLines, newSlots);
    },
    [script, currentBeat, beatIndex, fetchNextBeat, segmentParseOpts],
  );

  const advanceAfterBlank = useCallback(
    (word: string) => {
      if (!currentSegment || currentSegment.kind !== 'blank' || !script || !currentBeat) {
        return;
      }

      const newSlots = { ...slotValues, [currentSegment.id]: word };
      const newSegments = parseBeatSegments(
        currentBeat.template,
        newSlots,
        script,
        slotMorphRef.current,
        segmentParseOpts,
      );
      setSlotValues(newSlots);
      setBlankBuffer('');
      targetLenRef.current += word.length;

      const partial: RoundResult = {
        roundId: `${round.template.id}-b${beatIndex}-blank`,
        title: `${round.template.title} · hul`,
        mode,
        targetLength: word.length,
        durationMs: 0,
        wpm: 0,
        accuracy: 1,
        correctChars: word.length,
        totalKeystrokes: word.length,
        mistakes: 0,
        backspaces: 0,
        errorsByKey: {},
        responseTimes: {},
        completed: true,
        hintsUsed: false,
        presentationKind: 'story2',
      };
      mergeStats(statsRef.current, partial);
      playSuccessChime();

      const nextSeg = segmentIndex + 1;
      if (nextSeg < newSegments.length) {
        const next = newSegments[nextSeg];
        setSegmentIndex(nextSeg);
        setPhase(next?.kind === 'blank' ? 'typingBlank' : 'typingLiteral');
        return;
      }
      void finishBeat(newSlots);
    },
    [
      currentSegment,
      currentBeat,
      script,
      slotValues,
      segmentIndex,
      finishBeat,
      beatIndex,
      round.template.id,
      round.template.title,
      mode,
      segmentParseOpts,
    ],
  );

  const handleLiteralFinish = useCallback(
    (partial: RoundResult) => {
      mergeStats(statsRef.current, partial);
      targetLenRef.current += partial.correctChars;
      const nextSeg = segments[segmentIndex + 1];
      if (nextSeg?.kind === 'blank') {
        setSegmentIndex((i) => i + 1);
        setPhase('typingBlank');
      } else if (nextSeg?.kind === 'literal') {
        setSegmentIndex((i) => i + 1);
        setPhase('typingLiteral');
      } else {
        void finishBeat(slotValues);
      }
    },
    [segments, segmentIndex, slotValues, finishBeat],
  );

  const handleToggleHint = useCallback(() => {
    if (currentSegment?.kind === 'blank') {
      setShowHint((v) => !v);
    }
  }, [currentSegment?.kind]);

  const handleStart = useCallback(() => {
    setHasStarted(true);
    setFetchError(null);
  }, []);

  if (!seed || !script || !seedId) {
    return (
      <p className="text-center text-sm text-rose-600 dark:text-rose-300">
        Fortællingen kunne ikke indlæses.
      </p>
    );
  }

  if (!currentBeat && !storyComplete) {
    if (phase === 'loading') {
      return (
        <div className="flex flex-col gap-4">
          <p className="label text-faint">Fortælling 2 · {script.title}</p>
          <div className="surface p-5 sm:p-6">
            <StoryThinkingLoader />
          </div>
        </div>
      );
    }
    return null;
  }

  if (!hasStarted) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <p className="label text-faint">
            Fortælling 2 · {script.title}
            {apiMode === 'mock' ? ' · demo' : ''}
          </p>
        </div>
        <div className="surface flex flex-col items-center gap-4 p-8 text-center sm:p-10">
          <p className="text-lg font-semibold text-strong">
            Klar til {script.title.toLowerCase()}?
          </p>
          <p className="max-w-md text-sm text-muted">
            Læs og skriv sætningerne på skærmen. Nogle gange mangler et ord —
            det finder du selv på, og historien fortsætter.
          </p>
          {apiMode === 'mock' && (
            <p className="text-xs text-amber-800 dark:text-amber-200">
              Demo-fortælling (ingen OpenAI-nøgle på serveren).
            </p>
          )}
          <button
            type="button"
            onClick={handleStart}
            className="btn-primary px-8 py-3 text-base font-semibold"
          >
            Start historien
          </button>
        </div>
      </div>
    );
  }

  if (storyComplete) {
    return (
      <Story2CompleteScreen
        title={script.title}
        lines={completedLines}
        onPlayAnother={playAnotherStory}
        onDashboard={exitToDashboard}
      />
    );
  }

  const visHintLabel =
    currentSegment?.kind === 'blank'
      ? showHint
        ? 'Skjul hint'
        : 'Vis hint'
      : '';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <p className="label text-faint">
          Fortælling 2 · {script.title}
          {apiMode === 'mock' ? ' · demo' : ''}
        </p>
        <p className="text-xs text-faint">Linje {beatIndex + 1}</p>
      </div>

      {completedLines.length > 0 && (
        <div className="surface max-h-[min(30vh,12rem)] overflow-y-auto p-4 sm:p-5">
          <p className="label mb-2 text-faint">Din historie</p>
          <div className="space-y-1 font-mono text-base leading-relaxed">
            {completedLines.map((line, i) => (
              <p
                key={i}
                className="text-emerald-700 dark:text-emerald-300"
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="surface flex flex-col gap-4 p-5 sm:p-6">
        {fetchError && (
          <div className="rounded-xl bg-rose-100 px-4 py-2 text-center text-xs text-rose-900 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-200 dark:ring-rose-500/30">
            {fetchError}
            <button
              type="button"
              className="ml-2 font-semibold underline"
              onClick={() =>
                void fetchNextBeat(
                  completedLinesRef.current,
                  slotValuesRef.current,
                )
              }
            >
              Prøv igen
            </button>
          </div>
        )}

        {phase === 'loading' ? (
          <StoryThinkingLoader />
        ) : phase === 'typingLiteral' &&
          currentSegment?.kind === 'literal' ? (
          <Story2LiteralTyper
            key={`${beatIndex}-${segmentIndex}`}
            segment={currentSegment}
            mode={mode}
            roundId={`${round.template.id}-b${beatIndex}-s${segmentIndex}`}
            roundTitle={round.template.title}
            keyboardLayout={keyboardLayout}
            trin={trin}
            strictCase={strictCase}
            allowAltLayer={allowAltLayer}
            onFinish={handleLiteralFinish}
          />
        ) : currentSegment ? (
          <Story2LineDisplay
            segments={segments}
            segmentIndex={segmentIndex}
            phase={phase}
            slotValues={slotValues}
            blankBuffer={blankBuffer}
            showHint={showHint}
            blankHint={blankHint}
          />
        ) : null}

        {currentSegment?.kind === 'blank' && visHintLabel && (
          <button
            type="button"
            onClick={handleToggleHint}
            className="btn-secondary py-2.5 text-sm font-semibold"
          >
            {visHintLabel}
          </button>
        )}
      </div>

      {phase === 'typingBlank' && currentSegment?.kind === 'blank' && (
        <>
          <Story2BlankTyper
            maxLength={currentSegment.maxLength}
            setBuffer={setBlankBuffer}
            onDone={advanceAfterBlank}
          />
          <div className="space-y-2">
            <Keyboard
              layoutId={keyboardLayout}
              unlockedKeys={trin.unlockedKeys}
              hintLevel="none"
            />
          </div>
        </>
      )}


      <p className="text-center text-xs text-faint">
        Skriv sætningen på skærmen. Huller er understregninger — dit ord bliver
        en del af historien.
      </p>
    </div>
  );
}

interface Story2LineDisplayProps {
  segments: StorySegment[];
  segmentIndex: number;
  phase: SegmentPhase;
  slotValues: Record<string, string>;
  blankBuffer: string;
  showHint: boolean;
  blankHint?: string;
}

function Story2LineDisplay({
  segments,
  segmentIndex,
  phase,
  slotValues,
  blankBuffer,
  showHint,
  blankHint,
}: Story2LineDisplayProps) {
  const parts: ReactNode[] = [];
  let key = 0;

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    if (seg.kind === 'literal') {
      const isPast = i < segmentIndex;
      const isCurrent = i === segmentIndex && phase === 'typingLiteral';
      if (isPast) {
        parts.push(
          <span
            key={key++}
            className="text-emerald-600 dark:text-emerald-300"
          >
            {seg.text}
          </span>,
        );
      } else if (isCurrent) {
        parts.push(
          <span key={key++} className="text-zinc-500 dark:text-zinc-400">
            {seg.text}
          </span>,
        );
      } else {
        parts.push(
          <span key={key++} className="text-zinc-400 dark:text-zinc-500">
            {seg.text}
          </span>,
        );
      }
    } else {
      const filled = slotValues[seg.id];
      const active = i === segmentIndex && phase === 'typingBlank';
      const done = Boolean(filled) && (i < segmentIndex || !active);
      if (done && filled) {
        parts.push(
          <span
            key={key++}
            className="text-emerald-600 dark:text-emerald-300"
          >
            {filled}
          </span>,
        );
      } else {
        parts.push(
          <BlankInline
            key={key++}
            maxLength={seg.maxLength}
            buffer={active ? blankBuffer : ''}
            active={active}
          />,
        );
      }
    }
  }

  return (
    <div className="flex min-h-[7rem] flex-col items-center justify-center gap-3">
      <p className="max-w-full overflow-x-auto whitespace-pre-wrap text-center font-mono text-2xl leading-relaxed tracking-wide sm:text-3xl">
        {parts}
      </p>
      {showHint && blankHint ? (
        <p className="max-w-md text-center text-sm text-muted">{blankHint}</p>
      ) : (
        <p className="text-xs text-faint">_ = dit eget ord i historien</p>
      )}
    </div>
  );
}

function BlankInline({
  maxLength,
  buffer,
  active,
}: {
  maxLength: number;
  buffer: string;
  active: boolean;
}) {
  const width = blankDisplayWidth(maxLength);
  const chars: ReactNode[] = [];
  for (let i = 0; i < width; i++) {
    const ch = active ? buffer[i] : buffer[i];
    if (ch) {
      chars.push(
        <span
          key={i}
          className="border-b-2 border-indigo-400 text-emerald-600 dark:text-emerald-300"
        >
          {ch}
        </span>,
      );
    } else {
      chars.push(
        <span
          key={i}
          className="border-b-2 border-zinc-300 text-zinc-400 dark:border-zinc-600 dark:text-zinc-500"
        >
          _
        </span>,
      );
    }
  }
  return <span>{chars}</span>;
}

interface Story2LiteralTyperProps {
  segment: Extract<StorySegment, { kind: 'literal' }>;
  mode: PracticeMode;
  roundId: string;
  roundTitle: string;
  keyboardLayout: KeyboardLayoutId;
  trin: Trin;
  strictCase?: boolean;
  allowAltLayer?: boolean;
  onFinish: (r: RoundResult) => void;
}

function Story2LiteralTyper({
  segment,
  mode,
  roundId,
  roundTitle,
  keyboardLayout,
  trin,
  strictCase,
  allowAltLayer,
  onFinish,
}: Story2LiteralTyperProps) {
  const input = useRoundInput({
    targetText: segment.text,
    mode,
    roundId,
    roundTitle,
    presentationKind: 'story2',
    onFinish,
    strictCase,
    allowAltLayer,
    keyboardLayoutId: keyboardLayout,
  });

  return (
    <>
      <div className="flex min-h-[7rem] flex-col items-center justify-center gap-3">
        <p className="max-w-full whitespace-pre-wrap break-normal text-center font-mono text-2xl leading-relaxed tracking-wide sm:text-3xl">
          {renderStory2LiteralTyped({
            targetText: segment.text,
            cursor: input.cursor,
            correctMask: input.correctMask,
          })}
        </p>
        <p className="text-xs text-faint">
          Skriv det du ser — grønt er korrekt
        </p>
      </div>
      <div className="space-y-2">
        <Keyboard
          layoutId={keyboardLayout}
          unlockedKeys={trin.unlockedKeys}
          nextExpectedKey={input.expectedChar}
          lastPressedKey={input.lastPress?.key}
          lastPressCorrect={input.lastPress?.correct}
          hintLevel={input.hintLevel}
          showFingerDot={input.hintLevel !== 'none' && mode !== 'guided'}
        />
        <SpaceAdvanceHint />
      </div>
    </>
  );
}

interface LiteralInputVisual {
  targetText: string;
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
}

function renderStory2LiteralTyped(input: LiteralInputVisual): ReactNode {
  const { targetText, cursor, correctMask } = input;
  const letters: ReactNode[] = [];
  for (let i = 0; i < targetText.length; i++) {
    const typed = correctMask[i];
    const isCursor = i === cursor;
    const ch = targetText[i]!;

    if (typed) {
      letters.push(
        <span key={i} className="text-emerald-600 dark:text-emerald-300">
          {ch}
        </span>,
      );
    } else if (isCursor) {
      letters.push(
        <span
          key={i}
          className="inline-block min-w-[0.6ch] rounded-sm border-l-2 border-indigo-500 bg-indigo-100 text-zinc-900 dark:bg-indigo-500/10 dark:text-zinc-100"
        >
          {ch === ' ' ? '\u00A0' : ch}
        </span>,
      );
    } else {
      letters.push(
        <span key={i} className="text-zinc-400 dark:text-zinc-500">
          {ch}
        </span>,
      );
    }
  }
  return <>{letters}</>;
}

function StoryThinkingLoader() {
  return (
    <div
      className="flex flex-col items-center justify-center gap-4 py-12"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="relative flex h-12 w-12 items-center justify-center">
        <span className="absolute inset-0 animate-ping rounded-full bg-indigo-400/30" />
        <span className="absolute inset-1 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-500 dark:border-indigo-500/30 dark:border-t-indigo-400" />
        <span className="relative h-2 w-2 rounded-full bg-indigo-500 dark:bg-indigo-400" />
      </div>
      <div className="max-w-sm text-center">
        <p className="text-sm font-medium text-strong">
          Vent et øjeblik mens historien tænker
        </p>
        <p className="mt-1 text-xs text-muted">
          Næste linje skrives…
        </p>
      </div>
    </div>
  );
}

interface Story2BlankTyperProps {
  maxLength: number;
  setBuffer: Dispatch<SetStateAction<string>>;
  onDone: (word: string) => void;
}

function Story2BlankTyper({
  maxLength,
  setBuffer,
  onDone,
}: Story2BlankTyperProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (shouldIgnoreKeyEvent(e)) return;

      if (e.key === 'Backspace') {
        e.preventDefault();
        setBuffer((b) => b.slice(0, -1));
        return;
      }

      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        setBuffer((b) => {
          const w = b.trim();
          if (w.length > 0) onDone(w);
          return '';
        });
        return;
      }

      if (!isPrintableTypingKey(e.key) || e.key === ' ') return;

      setBuffer((b) => {
        if (b.length >= maxLength) return b;
        return b + e.key;
      });
      e.preventDefault();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [maxLength, setBuffer, onDone]);

  return (
    <p className="text-center text-xs text-faint">
      Skriv dit ord — Enter når du er færdig
    </p>
  );
}

interface Story2CompleteScreenProps {
  title: string;
  lines: string[];
  onDashboard: () => void;
  onPlayAnother: () => void;
}

function Story2CompleteScreen({
  title,
  lines,
  onDashboard,
  onPlayAnother,
}: Story2CompleteScreenProps) {
  useEnterAction(onDashboard);

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-8 py-6 animate-fade-in-up">
      <header className="text-center">
        <p className="label text-faint">Din historie</p>
        <h2 className="mt-1 text-2xl font-semibold text-strong">{title}</h2>
      </header>

      <div className="surface w-full px-6 py-8 sm:px-10 sm:py-10">
        <div className="space-y-3 text-center font-mono text-xl leading-relaxed tracking-wide sm:text-2xl">
          {lines.map((line, i) => (
            <p key={i} className="text-emerald-700 dark:text-emerald-300">
              {line}
            </p>
          ))}
        </div>
      </div>

      <div className="w-full max-w-md space-y-3 text-center">
        <p className="text-sm text-muted">
          Vil du skrive en historie til, eller vende tilbage til oversigten?
        </p>
        <button
          type="button"
          onClick={onPlayAnother}
          className="w-full rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400"
        >
          En historie til
        </button>
        <button
          type="button"
          autoFocus
          onClick={onDashboard}
          className="w-full rounded-xl bg-zinc-200 px-6 py-3 text-base font-semibold text-zinc-900 transition hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
        >
          Tilbage til dashboard
        </button>
      </div>
    </div>
  );
}
